import './Main.css'

import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { useChecklistSubmit, WizardData } from './Model'
import { goToAddFailurePage, StartAddFailurePage } from '@/pages/communal/machine/failures/add/Main'
import { Machine } from '@/services/model/machine'
import { MachineName } from '@/components/machine'
import { useNavigate } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from '@/components/spinner'

export interface SimpleUserGroup {
  id: string
  name: string
}

const stepId = 'activate'

export const ActivateStep = ({ machine }: {
  machine: Machine
}) => {
  const { services, session, translates } = useAppContext()
  const navigate = useNavigate()
  const submitChecklist = useChecklistSubmit(machine.id)
  const { wizardData, updateWizardData, nextStep } = useWizard<WizardData>()
  const [generalError, setGeneralError] = useState(false)
  const [spinner, setSpinner] = useState(false)

  const [distanceError, setDistanceError] = useState(false)
  const [distanceOk, setDistanceOk] = useState<boolean | null>(null)

  const machineLat = machine.params?.deviceData?.lat
  const machineLng = machine.params?.deviceData?.lng

  useEffect(() => {
    let cancelled = false
    let intervalId: number | undefined

    // reset when machine coords change
    setDistanceError(false)
    setDistanceOk(null)

    const checkDistance = () => {
      const lat = typeof machineLat === 'string' ? Number(machineLat) : machineLat
      const lng = typeof machineLng === 'string' ? Number(machineLng) : machineLng

      if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {
        if (!cancelled) {
          setDistanceError(true)
          setDistanceOk(false)
        }
        return
      }

      if (!navigator.geolocation) {
        if (!cancelled) {
          setDistanceError(true)
          setDistanceOk(false)
        }
        return
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return

          const toRad = (v: number) => (v * Math.PI) / 180
          const earthRadiusMeters = 6371000

          const lat1Rad = toRad(lat)
          const lat2Rad = toRad(pos.coords.latitude)
          const deltaLatRad = toRad(pos.coords.latitude - lat)
          const deltaLngRad = toRad(pos.coords.longitude - lng)

          const sinDeltaLat = Math.sin(deltaLatRad / 2)
          const sinDeltaLng = Math.sin(deltaLngRad / 2)

          const a =
            sinDeltaLat * sinDeltaLat +
            Math.cos(lat1Rad) * Math.cos(lat2Rad) * sinDeltaLng * sinDeltaLng

          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
          const distanceMeters = earthRadiusMeters * c

          const ok = distanceMeters < 1000
          setDistanceOk(ok)
          setDistanceError(!ok)
        },
        () => {
          if (cancelled) return
          setDistanceError(true)
          setDistanceOk(false)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    }

    // run immediately, then refresh every 5 seconds
    checkDistance()
    intervalId = window.setInterval(checkDistance, 5000)

    return () => {
      cancelled = true
      if (intervalId !== undefined) window.clearInterval(intervalId)
    }
  }, [machineLat, machineLng])

  const machineActivate = useCallback(async (): Promise<void> => {
    setGeneralError(false)
    setSpinner(true)

    try {
      const resp = await services.session.fetchWithToken(`/api/communal/machines/byid/${machine.id}/activate`, {
        method: "POST"
      })

      if (!resp.ok) {
        setGeneralError(true)
        return
      }

      updateWizardData({ acceptedSteps: [...wizardData.acceptedSteps, stepId] })
      nextStep()
    } finally {
      setSpinner(false)
    }
  }, [machine.id, services.session, nextStep, updateWizardData, wizardData.acceptedSteps])

  if (translates === undefined || wizardData === undefined) {
    return <div className='spinner'></div>
  }

  return <div>
    <h3>{translates.for('machine.checklist.wizard.activate.title')}</h3>
    <p className='desc'>
      {translates.for('machine.checklist.wizard.activate.description')}
    </p>

    {spinner || distanceOk === null ? <Spinner /> : <div className='btns'>
      {distanceOk === true ? <div>
        <a className='btn btn-next main-btn' onClick={machineActivate}>
          {translates.for('machine.checklist.wizard.activate.approve')}
        </a>

        <a className='btn btn-fail main-red-btn' onClick={async () => {
          if (!await submitChecklist('fail', stepId)) return
          goToAddFailurePage({
            navigate,
            machineId: machine.id
          })
        }}>
          {translates.for('machine.checklist.wizard.activate.fail')}
        </a>
      </div> : <div>
        <a className='btn btn-next main-btn' onClick={() => navigate(`/communal/support`)}>
          {translates.for('machine.checklist.wizard.activate.support')}
        </a>
        <a className='btn btn-next main-btn' onClick={() => navigate(`/communal/support/wizard`)}>
          {translates.for('machine.checklist.wizard.activate.support_form')}
        </a>
      </div>}
      {distanceError ? <div className="critic-error-box">{translates?.for('errors.machine_distance_error')}</div> : null}
      {generalError ? <div className="critic-error-box">{translates?.for('errors.general_error')}</div> : null}
    </div>}
  </div>
}
